import React, { useMemo } from "react";
import { Car } from "lucide-react";
import Autocomplete from "@/components/Autocomplete";
import { VEHICLE_MAKES, MODELS_BY_MAKE } from "@/lib/vehicleData";
import { Step, Field } from "@/pages/booking/bookingUi";

const STATES = ["TAS", "VIC", "NSW", "QLD", "SA", "WA", "NT", "ACT"];

export const VehicleStep = ({ form, setForm, savedVehicles, applySavedVehicle }) => {
  // Models filtered by the picked make. If the make isn't in our list, fall back to all models.
  const modelOptions = useMemo(() => {
    const m = (form.vehicle_make || "").trim();
    const matched = VEHICLE_MAKES.find((mk) => mk.toLowerCase() === m.toLowerCase());
    if (matched && MODELS_BY_MAKE[matched]) return MODELS_BY_MAKE[matched];
    return Array.from(new Set(Object.values(MODELS_BY_MAKE).flat())).sort();
  }, [form.vehicle_make]);

  return (
    <Step n="03" title="Vehicle details" Icon={Car}>
      {savedVehicles.length > 0 && (
        <div className="mb-5" data-testid="saved-vehicle-chips">
          <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Your garage — tap to fill</p>
          <div className="flex flex-wrap gap-2">
            {savedVehicles.map((v) => {
              const active = form.vehicle_make === v.make && form.vehicle_model === v.model
                && String(form.vehicle_year) === String(v.year) && (form.vehicle_rego || "") === (v.rego || "");
              return (
                <button key={v.id} type="button" data-testid={`saved-vehicle-chip-${v.id}`} onClick={() => applySavedVehicle(v)}
                  className={`px-3 py-1.5 border text-xs transition-colors ${active ? "border-brand-red bg-red-500/10 text-brand-red" : "border-zinc-700 text-zinc-300 hover:border-brand-red hover:text-brand-red"}`}>
                  {v.year} {v.make} {v.model}{v.rego ? ` · ${v.rego}` : ""}
                </button>
              );
            })}
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <Field label="Make">
          <Autocomplete
            testId="booking-make"
            inputClassName="ta-input"
            value={form.vehicle_make}
            onChange={(v) => setForm({ ...form, vehicle_make: v, vehicle_model: "" })}
            options={VEHICLE_MAKES}
            placeholder="Toyota"
          />
        </Field>
        <Field label="Model">
          <Autocomplete
            testId="booking-model"
            inputClassName="ta-input"
            value={form.vehicle_model}
            onChange={(v) => setForm({ ...form, vehicle_model: v })}
            options={modelOptions}
            placeholder={form.vehicle_make ? "HiLux" : "Pick make first or just type"}
          />
        </Field>
        <Field label="Year"><input data-testid="booking-year" required type="number" min="1900" max="2099" className="ta-input" value={form.vehicle_year} onChange={(e) => setForm({ ...form, vehicle_year: e.target.value })} /></Field>
        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-2">
            <Field label="Rego (optional)"><input data-testid="booking-rego" className="ta-input" value={form.vehicle_rego} onChange={(e) => setForm({ ...form, vehicle_rego: e.target.value.toUpperCase() })} placeholder="ABC123" /></Field>
          </div>
          <div className="col-span-1">
            <Field label="State">
              <select data-testid="booking-state" className="ta-input" value={form.vehicle_state} onChange={(e) => setForm({ ...form, vehicle_state: e.target.value })}>
                {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
          </div>
        </div>
      </div>
    </Step>
  );
};
